import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient, { ApiResponse } from "./apiClient";

export interface LoyaltyTier {
  id: string;
  name: string;
  min_points: number;
  max_points: number;
  color: string;
  is_active: boolean;
}

export interface LoyaltyTierPayload {
  name: string;
  min_points: number;
  color: string;
}

const QUERY_KEY = ["loyalty"];

const fetchLoyaltyTiers = async (): Promise<LoyaltyTier[]> => {
  const { data: response } = await apiClient.get<ApiResponse<LoyaltyTier[]>>("/v1/admin/loyalty");
  return response.data.filter((tier) => tier.is_active);
};

export const useLoyaltyTiers = () =>
  useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchLoyaltyTiers,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });


const addLoyaltyTier = async (body: LoyaltyTierPayload): Promise<LoyaltyTier> => {
  const { data: response } = await apiClient.post<ApiResponse<LoyaltyTier>>("/v1/admin/loyalty", body);
  return response.data;
};

export const useAddLoyaltyTier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addLoyaltyTier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const updateLoyaltyTier = async ({ id, ...body }: LoyaltyTierPayload & { id: string }): Promise<LoyaltyTier> => {
  const { data: response } = await apiClient.put<ApiResponse<LoyaltyTier>>(`/v1/admin/loyalty/${id}`, body);
  return response.data;
};

export const useUpdateLoyaltyTier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLoyaltyTier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

const deleteLoyaltyTier = async (id: string): Promise<void> => {
  await apiClient.delete(`/v1/admin/loyalty/${id}`);
};

export const useDeleteLoyaltyTier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLoyaltyTier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
};

// ── Tiers Summary ─────────────────────────────────────────────────────────────

export interface TierSummaryItem {
  tier_id: number;
  tier_name: string;
  min_points: number;
  max_points: number;
  color: string;
  customer_count: number;
  percentage: number;
}

export interface TiersSummary {
  total_customers: number;
  tiers: TierSummaryItem[];
}

const TIERS_SUMMARY_QUERY_KEY = ["loyalty", "tiers-summary"];

const fetchTiersSummary = async (): Promise<TiersSummary> => {
  const { data: response } = await apiClient.get<ApiResponse<TiersSummary>>("/v1/admin/loyalty/tiers-summary");
  return response.data;
};

export const useTiersSummary = () =>
  useQuery({
    queryKey: TIERS_SUMMARY_QUERY_KEY,
    queryFn: fetchTiersSummary,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// ── Loyalty Config ────────────────────────────────────────────────────────────

export interface LoyaltyConfig {
  point_value_in_pence: number;
  referrer_points: number;
  new_customer_reward_type: string;
  new_customer_reward_value: number;
}

interface LoyaltyConfigResponse {
  loyalty: {
    id: number;
    program_name: string;
    point_value_in_pence: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  referral: {
    new_customer_reward_type: string;
    new_customer_reward_value: number;
    referrer_points: number;
  };
}

const CONFIG_QUERY_KEY = ["loyalty", "config"];

const fetchLoyaltyConfig = async (): Promise<LoyaltyConfig> => {
  const { data: response } = await apiClient.get<ApiResponse<LoyaltyConfigResponse>>("/v1/admin/program/config");
  const { loyalty, referral } = response.data;
  return {
    point_value_in_pence: loyalty.point_value_in_pence / 100,
    referrer_points: referral.referrer_points,
    new_customer_reward_type: referral.new_customer_reward_type,
    new_customer_reward_value: referral.new_customer_reward_value,
  };
};

export const useLoyaltyConfig = () =>
  useQuery({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: fetchLoyaltyConfig,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

const updateLoyaltyConfig = async (body: LoyaltyConfig): Promise<LoyaltyConfig> => {
  await apiClient.put<ApiResponse<LoyaltyConfigResponse>>("/v1/admin/program/config", {
    ...body,
    point_value_in_pence: body.point_value_in_pence * 100,
  });
  return body;
};

export const useUpdateLoyaltyConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLoyaltyConfig,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CONFIG_QUERY_KEY }),
  });
};
